<template>
	<common-card title="Woocommerce Settings" type="minimize">
		<template v-slot:title>
			<span class="advanced-settings-card-title">Woocommerce Settings <span class="advanced-settings-enabled-flag" :class="{'advanced-settings-enabled': hasWooSettings}">Enabled</span></span>
		</template>

		<div class="omapi-plugin-settings" :class="`omapi-plugin-settings__${isWooConnected ? 'active' : 'inactive'}`">
			<template v-if="isWooConnected">
				<label>
					<div>
						<common-tooltip
							tooltip="Select output settings specifically for your WooCommerce store."
							class="setting-label inline"
						>
								<span>Show on WooCommerce Pages:</span>
						</common-tooltip>
						<core-button
							type="secondary omapi-plugin-settings__select-all"
							@click.prevent="selectAllOrNone"
						>{{ selectAll ? 'Select None' : 'Select All' }}</core-button>
					</div>
				</label>

				<div class="omapi-radio-group omapi-input">
					<common-selectable-input
						v-for="(setting) in contextualSettings"
						type="checkbox"
						class="text-setting"
						:class="{'text-setting-indented': setting.isEndpoint}"
						:value="values[setting.id]"
						:checked="isChecked(setting)"
						:key="setting.id"
						:name="setting.id"
						:label="setting.label"
						@input="setWooValue"
					/>
				</div>

				<campaigns-taxonomies-settings
					v-if="!isLoading"
					:campaign="campaign"
					:taxonomies="wooTaxonomies"
					labelPrefix="Show on WooCommerce "
				/>
			</template>

			<template v-else-if="!isLoading">
				<div class="omapi-plugin-settings__logo">
					<img src="../../assets/images/logo-woocommerce.png" alt="WooCommerce Logo">
				</div>
				<div class="omapi-plugin-settings__content">
					<div class="omapi-plugin-settings__text">Go to <strong>OptinMonster Settings</strong> to integrate WooCommerce with the display rules in the campaign builder.</div>
					<core-button size="medium"
						color="blue"
						:isLink="true"
						:routerTo="{ name: 'settings-tab', params: { selectedTab: 'general' }}"
					>
						Go To OptinMonster Settings
					</core-button>
				</div>
			</template>
		</div>

		<slot/>
	</common-card>
</template>

<script lang="js">
import { mapState, mapGetters } from 'vuex'
import { campaign } from '@/campaigns/mixins/campaign'
import { wooSettings } from '@/campaigns/data'

export default  {
	mixins : [ campaign ],
	props  : {
		campaign : {
			type     : Object,
			required : true,
		},
	},
	computed : {
		...mapState('wp', [ 'taxonomies' ]),
		...mapGetters('wp', [ 'isWooConnected' ]),
		isLoading () {
			return !this.$store.getters.isFetched('wpResources')
		},
		contextualSettings () {
			return wooSettings.filter(setting => {
				return !setting.show || setting.show === this.campaignType
			}).map(s => {
				s.isEndpoint = this.isEndpoint(s.id)
				return s
			})
		},
		values () {
			const values = {}
			wooSettings.forEach(setting => {
				const key = `campaign.wp.post_meta._omapi_${setting.id}`
				values[setting.id] = '1' === this.$get(key) ? '1' : '0'
			})

			return values
		},
		selectAll () {
			return '1' === this.values.show_on_woocommerce
		},
		allEndpoints () {
			return '1' === this.values.is_wc_endpoint
		},
		wooTaxonomies () {
			return this.filterTaxonomies((tax) => {
				return this.isWooTaxonomy(tax) && 0 < tax.terms.length
			})
		},
		/**
		 * Whether any of the woo settings have been set.
		 *
		 * @since 2.8.0
		 *
		 * @returns {boolean} If campaign has advanced settings set.
		 */
		hasWooSettings () {
			// Check if any woocommerce taxonomy was selected
			const wooTaxonomySelected = Object.entries(this.$get('$store.state.wp.taxonomies', {})).some(([ key, taxonomy ]) => {
				if (!this.isWooTaxonomy(taxonomy)) {
					return false
				}

				const value = this.$get(
					`campaign.wp.post_meta._omapi_${key}`,
					this.$get(`campaign.wp.post_meta._omapi_taxonomies.${key}`, [])
				)

				return value && value.length
			})

			if (wooTaxonomySelected) {
				return true
			}

			// Check if any setting is active for woo
			return wooSettings.some(setting => {
				return '1' === this.$get(`campaign.wp.post_meta._omapi_${setting.id}`)
			})
		},
	},
	methods : {
		doShow (id) {
			return '1' === this.values[id]
		},
		isEndpoint (id) {
			return 0 === id.indexOf('is_wc_endpoint_')
		},
		isChecked (setting) {
			return this.selectAll || this.doShow(setting.id) || (this.allEndpoints && setting.isEndpoint)
		},
		setWooValue ({ target }) {
			const { name : id } = target

			const value = '1' === this.values[id] ? '0' : '1'

			this.updateValue(id, value)

			this.setChecked(id, value)
		},
		setChecked (id, value) {
			const allChecked = this.contextualSettings.every(s => '1' === this.values[s.id])

			this.updateValue('show_on_woocommerce', allChecked ? '1' : '0')

			if (!this.isEndpoint(id)) {
				return
			}

			const allEndpointsChecked = this.contextualSettings.every(s => {
				return !s.isEndpoint || this.doShow(s.id)
			})

			this.updateValue('is_wc_endpoint', allEndpointsChecked ? '1' : '0')
		},
		selectAllOrNone () {
			this.updateValue('show_on_woocommerce', this.selectAll ? '0' : '1')
		},
		updateValue (id, value) {
			this.updateCampaignValue({
				campaignId : this.id,
				key        : `wp.post_meta._omapi_${id}`,
				value,
				valueType  : 'numericBool',
			})
		},
	},
}
</script>
<style>
	.text-setting-indented {
		padding-left: 26px;
	}
</style>
